<script setup lang="ts">
import {
  ArrowUpRight,
  CircleHelp,
  ShieldCheck,
  Sparkles,
  Wrench,
  Zap,
  TriangleAlert,
} from '@lucide/vue'
import type { PresentedRelease } from '~~/shared/schemas/changelog'

defineProps<{ release: PresentedRelease }>()
const labels: Record<string, string> = {
  new: 'Nouveautés',
  improvement: 'Améliorations',
  fix: 'Corrections',
  security: 'Sécurité',
  breaking: 'Changements majeurs',
  other: 'Autres',
}
const icons = {
  new: Sparkles,
  improvement: Zap,
  fix: Wrench,
  security: ShieldCheck,
  breaking: TriangleAlert,
  other: CircleHelp,
}
</script>

<template>
  <article class="changelog-release-card analytics-sensitive">
    <div class="changelog-release-heading">
      <div class="changelog-release-meta">
        <span class="changelog-version">v{{ release.version }}</span>
        <span
          class="changelog-meta-separator"
          aria-hidden="true"
        />
        <time :datetime="release.date">{{
          new Date(`${release.date}T00:00:00Z`).toLocaleDateString('fr-FR', { dateStyle: 'long' })
        }}</time>
      </div>
      <span
        v-if="release.critical"
        class="changelog-critical"
        ><ShieldCheck :size="14" /> Critique</span
      >
    </div>
    <h2 class="changelog-release-title">{{ release.title }}</h2>
    <p class="changelog-release-summary">{{ release.summary }}</p>
    <img
      v-if="release.image"
      class="changelog-image"
      :src="release.image"
      alt="Illustration de la release"
      loading="lazy"
    />
    <section
      v-for="(section, index) in release.sections"
      :key="index"
      class="changelog-section"
    >
      <div
        class="changelog-section-icon"
        :data-category="section.type"
        aria-hidden="true"
      >
        <component
          :is="icons[section.type]"
          :size="18"
          :stroke-width="1.9"
        />
      </div>
      <div class="changelog-section-content">
        <h3>{{ section.title || labels[section.type] }}</h3>
        <span
          v-if="section.title"
          class="changelog-category"
          >{{ labels[section.type] }}</span
        >
        <div
          v-for="(entry, entryIndex) in section.entries"
          :key="entryIndex"
          class="changelog-entry"
        >
          <h4 v-if="entry.title">{{ entry.title }}</h4>
          <!-- HTML is rendered and sanitized by ChangelogService. -->
          <div
            class="changelog-markdown"
            v-html="entry.contentHtml"
          />
          <a
            v-if="entry.link"
            class="changelog-entry-link"
            :href="entry.link.url"
            :target="entry.link.target"
            rel="noopener noreferrer"
            >{{ entry.link.label }}
            <ArrowUpRight
              :size="15"
              aria-hidden="true"
          /></a>
        </div>
      </div>
    </section>
  </article>
</template>

<style lang="scss" src="~/assets/scss/sfc/components/changelog/ChangelogReleaseCard.scss"></style>
